import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, ListChecks } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", plan: "Monthly Audit", start_date: "" };

export const AdminClients = ({ selected, onSelect }) => {
  const [clients, setClients] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/portal/admin/clients`, { withCredentials: true })
    .then((r) => setClients(r.data.clients)).catch(() => setClients([]));

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/portal/admin/clients`, form, { withCredentials: true });
      toast.success(`${form.name} added.`);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add client.");
    } finally {
      setBusy(false);
    }
  };

  const seed = async (id) => {
    try {
      await axios.post(`${API}/portal/admin/clients/${id}/seed`, {}, { withCredentials: true });
      toast.success("30-day checklist created.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Seed failed.");
    }
  };

  const inputCls = "rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40";

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <form onSubmit={create} className="rounded-2xl border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input required placeholder="Client / business name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="client-input-name" />
        <input required type="email" placeholder="Client login email (their Google)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="client-input-email" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="client-input-phone" />
        <input placeholder="Plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={inputCls} data-testid="client-input-plan" />
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 shrink-0">Start</label>
          <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={`${inputCls} flex-1`} data-testid="client-input-start" />
        </div>
        <button type="submit" disabled={busy} data-testid="client-create-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Add Client
        </button>
      </form>

      {clients === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      ) : clients.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="clients-empty">No clients yet — add your first one above.</p>
      ) : (
        <div className="space-y-2">
          {clients.map((c) => (
            <div key={c.id} className={`flex flex-wrap items-center gap-3 rounded-2xl border px-5 py-4 ${selected?.id === c.id ? "border-white/40 bg-white/[0.05]" : "border-white/10"}`} data-testid={`client-row-${c.id.slice(0, 8)}`}>
              <button onClick={() => onSelect(c)} className="text-left flex-1 min-w-0" data-testid={`client-select-${c.id.slice(0, 8)}`}>
                <p className="font-display font-semibold text-white">{c.name}</p>
                <p className="text-xs text-slate-500">{c.email} · {c.plan} · started {c.start_date}</p>
              </button>
              <span className="font-mono-label text-xs text-slate-400">{c.tasks_done}/{c.tasks_total} done</span>
              {c.tasks_total === 0 && (
                <button onClick={() => seed(c.id)} data-testid={`client-seed-${c.id.slice(0, 8)}`} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]">
                  <ListChecks className="h-3.5 w-3.5" /> Seed 30-day checklist
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
